import { createFileRoute, Link } from "@tanstack/react-router";
import { ScrollText } from "lucide-react";
import { Layout } from "@/components/site/Layout";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms & Conditions | XAU Pro Academy" },
      {
        name: "description",
        content:
          "Terms of Service for XAU Pro Academy: subscription, payments via Stripe, cancellation and refunds, intellectual property, and limitation of liability.",
      },
      { property: "og:title", content: "Terms & Conditions | XAU Pro Academy" },
      {
        property: "og:description",
        content:
          "Read the Terms of Service that govern your use of XAU Pro Academy memberships and educational content.",
      },
    ],
    links: [{ rel: "canonical", href: "https://xauproacademy.com/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <Layout>
      <section className="container mx-auto max-w-3xl px-4 py-16">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gold-muted text-gold">
          <ScrollText className="h-7 w-7" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-bold md:text-4xl">
          Terms &amp; Conditions
        </h1>
        <p className="mt-3 text-muted-foreground">
          Please read these terms carefully before accessing XAU Pro Academy.
        </p>
        <p className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">
          Last Updated: October 2026
        </p>

        <div className="mt-10 space-y-8 text-sm leading-relaxed text-foreground/90">
          <section>
            <h2 className="font-display text-lg font-semibold">
              1. Acceptance of Terms
            </h2>
            <p className="mt-2 text-muted-foreground">
              By accessing or using XAU Pro Academy, you agree to be bound by these
              Terms of Service and by our{" "}
              <Link to="/risk-disclaimer" className="text-foreground underline-offset-2 hover:underline">
                Risk Disclaimer
              </Link>
              . If you do not agree to any part of these terms, you must stop using
              the platform immediately. Continued use constitutes ongoing acceptance.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              2. Subscription &amp; Payments
            </h2>
            <p className="mt-2 text-muted-foreground">
              Subscription payments are processed through{" "}
              <strong className="text-foreground">Stripe</strong>, our payment
              service provider, on behalf of our operating company Orion AI
              Technologies - F.Z.C. We handle invoicing, applicable sales taxes,
              and payment related customer service. By subscribing, you authorize
              Stripe to charge your selected payment method for the applicable
              membership fees. Subscriptions renew automatically at the end of
              each billing cycle until cancelled. You can cancel or manage your
              subscription at any time from your member dashboard.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              3. Cancellation &amp; Refunds
            </h2>
            <p className="mt-2 text-muted-foreground">
              You may cancel at any time in one step, and access continues until the end
              of the paid period. First-time subscribers may request a full refund within
              72 hours of the initial payment, and refunds are available for verified
              access failures not resolved within 48 hours. Full details are set out in
              our{" "}
              <Link to="/refund-policy" className="text-foreground underline-offset-2 hover:underline">
                Cancellation &amp; Refund Policy
              </Link>
              .
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              4. Intellectual Property
            </h2>
            <p className="mt-2 text-muted-foreground">
              All content on the platform, including trade case studies, written
              analysis, lessons, videos, charts, and the internal trade log, is the
              exclusive property of XAU Pro Academy. Sharing, reselling,
              redistributing, screen-recording, or otherwise reproducing this
              content without prior written permission is strictly prohibited and
              may result in immediate account termination without refund and further
              legal action.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              5. Limitation of Liability
            </h2>
            <p className="mt-2 text-muted-foreground">
              XAU Pro Academy and its operating company, Orion AI Technologies -
              F.Z.C, are not liable for any financial losses, damages, or claims
              arising from your use of the platform or from any trading decisions
              you make. All desk observations, alerts, and case studies are the
              automated output of proprietary algorithmic models governed
              under the Orion AI framework (including OTAS-01, OSAS-01, and
              all current, upgraded, and future quantitative or research
              models), not personalized investment advice from human analysts.
              You acknowledge that trading
              foreign exchange and precious metals carries a high level of risk and
              that you act entirely at your own discretion. See our{" "}
              <Link to="/risk-disclaimer" className="text-foreground underline-offset-2 hover:underline">
                Risk Disclaimer
              </Link>{" "}
              for the full scope of risks involved.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              6. Service Availability
            </h2>
            <p className="mt-2 text-muted-foreground">
              The platform is provided on an "as available" basis. Scheduled
              maintenance, data vendor outages, delayed or missed notifications,
              market closures, and engine pauses triggered by our risk controls
              (including macro event shields and circuit breakers) are normal
              operating conditions. They do not constitute a failure to deliver
              the service, a breach of these terms, or grounds for a refund or
              payment dispute. A period without published setups is a deliberate
              outcome of selective rules, not a service interruption.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              7. Subscriptions, Renewal and Cancellation
            </h2>
            <p className="mt-2 text-muted-foreground">
              Paid memberships are billed monthly or annually and renew
              automatically until cancelled. You may cancel at any time from your
              account or the billing portal; access continues until the end of
              the paid period. Refund eligibility is defined in our Cancellation
              and Refund Policy. Before opening a dispute
              with your card issuer, contact support@xauproacademy.com so we can
              resolve the matter directly. Disputes filed for services already
              accessed may lead to account suspension.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              8. Governing Entity
            </h2>
            <p className="mt-2 text-muted-foreground">
              XAU Pro Academy is operated by Orion AI Technologies - F.Z.C, a
              Free Zone Company with limited liability, licensed by the Free
              Zones Authority of Ajman under License and Registration No. 58070.
              Registered office: Office C1-1F-SF12185, Ajman Free Zone C1
              Building, Ajman Free Zone, United Arab Emirates (Makani No.
              4442612247). These terms are governed by the laws applicable in
              the Emirate of Ajman and the United Arab Emirates.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              9. Acceptable Use and Setup Protection
            </h2>
            <p className="mt-2 text-muted-foreground">
              Gold setups, levels, and trade observations published on the platform
              are research output of our proprietary quantitative models governed
              under the Orion AI framework (including OTAS-01, OSAS-01, and all
              current, upgraded, and future models), provided for educational
              and analytical purposes only. Each
              membership grants a personal, non-transferable, single-user licence.
              You may not share login credentials, use automated scraping or
              crawling tools, or copy, forward, rebroadcast, or resell any setup,
              level, alert, or analysis to Telegram groups, Discord servers, social
              media, or any third party, whether free or paid. We may suspend or
              permanently terminate any account found in breach immediately and
              without notice, with no refund of any remaining subscription period,
              and we reserve the right to pursue legal remedies.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              10. Third-Party Data and Price Feeds
            </h2>
            <p className="mt-2 text-muted-foreground">
              Market prices, candles, and economic data are sourced from
              independent third-party providers and are supplied "as is". Delays,
              gaps, inaccuracies, or interruptions in any external feed or in our
              real-time price relay do not constitute a breach of these terms and
              do not create any right to compensation or refund.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              11. Liability Cap
            </h2>
            <p className="mt-2 text-muted-foreground">
              To the maximum extent permitted by law, the total aggregate liability
              of Orion AI Technologies - F.Z.C for any claim arising from or related
              to the platform shall not exceed the total fees you paid to us during
              the twelve (12) months immediately preceding the event giving rise to
              the claim. We are not liable for indirect, incidental, or
              consequential losses, including lost profits or trading losses.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              12. Indemnification
            </h2>
            <p className="mt-2 text-muted-foreground">
              You agree to indemnify and hold harmless Orion AI Technologies -
              F.Z.C, its officers, and personnel from any claims, damages, losses,
              and expenses, including reasonable legal fees, arising from your
              misuse of the platform or your breach of these terms.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              13. No Reliance and Waiver of Claims
            </h2>
            <p className="mt-2 text-muted-foreground">
              You agree that you will not rely on any content, alert, level, or
              analysis published on the platform as the basis for any trading or
              investment decision, and that any decision you make is taken
              independently and at your own risk. To the maximum extent permitted
              by law, you waive any claim against Orion AI Technologies - F.Z.C
              arising from trading losses, missed opportunities, or decisions made
              in reliance on platform content.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              14. Changes to These Terms
            </h2>
            <p className="mt-2 text-muted-foreground">
              Administrative updates take effect when the "Last Updated" date is
              revised. Material changes will be notified at least 30 days in
              advance by email or in-platform notice. Continued use of the platform
              after changes take effect constitutes acceptance of the revised terms.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              15. Contact
            </h2>
            <p className="mt-2 text-muted-foreground">
              For any questions regarding these terms, please contact us at{" "}
              <a
                href="mailto:support@xauproacademy.com"
                className="text-foreground underline-offset-2 hover:underline"
              >
                support@xauproacademy.com
              </a>
              .
            </p>
          </section>

          <div className="rounded-xl border border-gold/30 bg-gold-muted p-5">
            <p className="font-mono text-xs font-semibold uppercase leading-relaxed tracking-wider text-foreground">
              By using XAU Pro Academy's services, you acknowledge that you have
              read, understood, and agree to be bound by this user agreement.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
